import React, {
  MouseEventHandler,
  useCallback,
  useEffect,
  useMemo,
} from 'react';
import Icon from '@/components/Icon';
import Panel from '@/components/Panel';
// import FetchUserNameEvent from '@/components/PollingEvent'; // 这里不能调用 FetchUserNameEvent，会和首页拉取用户名冲突
import ruleExecutor from '@shopee/banking-utils/src/rule';
import { compare } from '@/utils/version';
import { useTranslation } from 'react-i18next';
import { helpCenterTracking } from '@/utils/track';
import { getAppVersion, getNativeMethod, getOSInfo } from '@/utils/misc';
import { getHelpMethodsConfig, IItemProps } from './config';
import styles from './index.module.less';
import { jump } from '@/utils/bridgeHelper';

interface ItemCompProps extends IItemProps {
  onClick?: Function;
}

const Item = ({
  url,
  text,
  icon,
  condition,
  onClick,
  extras = {},
  isDeepLink = false,
}: ItemCompProps) => {
  const { t } = useTranslation();
  const shouldShow = useMemo(() => {
    if (!condition) return true;
    return ruleExecutor(
      condition,
      {
        is_login: extras.isLogin ? 'yes' : 'no',
        app_version: extras.appVersion,
        os: extras.os,
        os_version: extras.osVersion,
        from: extras.from,
      },
      {
        app_version: compare,
        os_version: compare,
      },
    );
  }, [condition, extras]);
  const fromHome = useMemo(() => extras.from === 'home', [extras]);

  const trackType = useMemo(() => {
    if (url.startsWith('tel')) {
      return 'call_btn';
    }
    if (url.startsWith('mailto')) {
      return 'email_btn';
    }
    if (url.includes('openLiveChatWeb')) {
      return 'btn_chat_now';
    }
    if (url.includes('facialpage')) {
      return 'facial_verification';
    }
  }, [url]);

  useEffect(() => {
    if (shouldShow) {
      if (fromHome) {
        setTimeout(() => {
          // 首页有轮询获取 user 信息的逻辑，这里需要延迟一点上报
          helpCenterTracking({
            operation: 'impression',
            target: trackType,
          });
        }, 100);
      } else {
        helpCenterTracking({
          operation: 'view',
          target: trackType,
          data: {
            article_url: window.location.href,
          },
        });
      }
    }
  }, [shouldShow, trackType, fromHome]);

  const onClickBtnTrack = useCallback(() => {
    if (fromHome) {
      helpCenterTracking({
        operation: 'click',
        target: trackType,
      });
    } else {
      helpCenterTracking({
        page: 'help_centre_faq',
        operation: 'click',
        target: trackType,
        data: {
          article_url: window.location.href,
        },
      });
    }
  }, [trackType, fromHome]);

  if (!shouldShow) return null;

  const linkProps: {
    onClick?: MouseEventHandler<HTMLAnchorElement>;
    href?: string;
  } = {};
  if (onClick) {
    linkProps.onClick = (e) => {
      onClick(e, url);
      onClickBtnTrack();
    };
  } else {
    linkProps.href = url;
    linkProps.onClick = () => {
      onClickBtnTrack();
    };
  }

  return isDeepLink ? (
    <div
      onClick={() => {
        jump(url);
      }}
    >
      <Panel.Item
        text={t(text)}
        iconName={icon}
        iconStyle={styles.icon}
        right={<Icon iconName={'arrow_right'} imgClass={styles.arrow} />}
      />
    </div>
  ) : (
    <a {...linkProps}>
      <Panel.Item
        text={t(text)}
        iconName={icon}
        iconStyle={styles.icon}
        right={<Icon iconName={'arrow_right'} imgClass={styles.arrow} />}
      />
    </a>
  );
};

const Footer: React.FC<{ isLogin?: boolean; from?: string }> = ({
  isLogin,
  from,
}) => {
  const { t } = useTranslation();
  const version = useMemo(getAppVersion, []);
  const { platform, version: osVersion } = useMemo(getOSInfo, []);

  const openNativePage = useCallback((e, url) => {
    const res = getNativeMethod(url);
    (window as MyWindow).WebViewJavascriptBridge.callHandler(
      res.method,
      res.params,
    );
    e.preventDefault();
  }, []);

  const getOnClickProp = (item) => {
    if (item.url.startsWith('native:')) {
      return (e, url) => openNativePage(e, url);
    }
    return null;
  };

  return (
    <div className={styles.panelBox}>
      <Panel header={t('other_help_method')}>
        {getHelpMethodsConfig(isLogin).map(
          (item) =>
            item?.isOpen && (
              <Item
                key={item.url}
                url={item.url}
                text={item.text}
                icon={item.icon}
                condition={item.rule}
                extras={{
                  isLogin,
                  appVersion: version,
                  os: platform,
                  osVersion,
                  from,
                }}
                isDeepLink={item.isDeepLink}
                onClick={getOnClickProp(item)}
              />
            ),
        )}
      </Panel>
    </div>
  );
};

export default Footer;
